import React, { useState, useEffect, useCallback, useRef } from 'react';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Header from './components/Header';
import CarGrid from './components/CarGrid';
import SearchAndFilters from './components/SearchAndFilters';
import CarModal from './components/CarModal';
import StatsPanel from './components/StatsPanel';
import Prospection from './components/Prospection';
import MotoMarket from './components/MotoMarket';
import MarketSummary from './components/MarketSummary';
import Seguimiento from './components/Seguimiento';
import ProspectionFormFromMarket from './components/ProspectionFormFromMarket';
import Toast from './components/Toast';
import ScrollToTop from './components/ScrollToTop';
import InstallPrompt from './components/InstallPrompt';
// import DebugInfo from './components/DebugInfo';
import { carService, prospectionService } from './services/api';
import { useDolarBlue } from './hooks/useDolarBlue';
import useInfiniteScroll from './hooks/useInfiniteScroll';

// Datos de ejemplo para desarrollo (reemplazar con datos reales de la API)
const mockCars = [
  {
    id: 'car_001',
    title: "Toyota Corolla 2020 - Excelente Estado",
    price: 18500,
    kilometers: 45000,
    pictures: "https://images.unsplash.com/photo-1621007947382-bb3c3994e3fb?w=400&h=300&fit=crop&crop=center",
    city: "Buenos Aires",
    price_history: [
      { price: 18500, date: '2024-01-15' },
      { price: 18550, date: '2023-12-15' },
      { price: 18600, date: '2023-11-15' },
      { price: 18800, date: '2023-10-15' }
    ]
  },
  {
    id: 'car_002',
    title: "Honda Civic Sport 2019 - Full Equipo",
    price: 22000,
    kilometers: 38000,
    pictures: "https://images.unsplash.com/photo-1606664515524-ed2f786a0bd6?w=400&h=300&fit=crop&crop=center",
    city: "Córdoba",
    price_history: [
      { price: 22000, date: '2024-01-15' },
      { price: 21900, date: '2023-12-15' },
      { price: 21800, date: '2023-11-15' },
      { price: 21500, date: '2023-10-15' }
    ]
  },
  {
    id: 'car_003',
    title: "Ford Focus Titanium 2021 - Como Nuevo",
    price: 25500,
    kilometers: 15000,
    pictures: "https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=400&h=300&fit=crop&crop=center",
    city: "Rosario",
    price_history: [
      { price: 25500, date: '2024-01-15' },
      { price: 25550, date: '2023-12-15' },
      { price: 25600, date: '2023-11-15' },
      { price: 25700, date: '2023-10-15' }
    ]
  },
  {
    id: 'car_004',
    title: "Chevrolet Cruze LTZ 2018 - Impecable",
    price: 16800,
    kilometers: 62000,
    pictures: "https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=400&h=300&fit=crop&crop=center",
    city: "Mendoza",
    price_history: [
      { price: 16800, date: '2024-01-15' },
      { price: 16850, date: '2023-12-15' },
      { price: 16900, date: '2023-11-15' },
      { price: 17100, date: '2023-10-15' }
    ]
  },
  {
    id: 'car_005',
    title: "Volkswagen Jetta GLI 2020 - Deportivo",
    price: 28000,
    kilometers: 28000,
    pictures: "https://images.unsplash.com/photo-1494905998402-395d579af36f?w=400&h=300&fit=crop&crop=center",
    city: "La Plata",
    price_history: [
      { price: 28000, date: '2024-01-15' },
      { price: 27800, date: '2023-12-15' },
      { price: 27500, date: '2023-11-15' },
      { price: 27200, date: '2023-10-15' }
    ]
  },
  {
    id: 'car_006',
    title: "Nissan Sentra Advance 2019 - Automático",
    price: 19500,
    kilometers: 41000,
    pictures: "https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=400&h=300&fit=crop&crop=center",
    city: "Mar del Plata",
    price_history: [
      { price: 19500, date: '2024-01-15' },
      { price: 19580, date: '2023-12-15' },
      { price: 19650, date: '2023-11-15' },
      { price: 19750, date: '2023-10-15' }
    ]
  }
];

function AppContent() {
  const [cars, setCars] = useState([]);
  const [filteredCars, setFilteredCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Inicializar cotización del dólar blue
  const { cotizacion, loading: dolarLoading } = useDolarBlue();
  
  // Estados para filtros y búsqueda
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('price_change');
  const [sortOrder, setSortOrder] = useState('desc');
  const [priceRange, setPriceRange] = useState([0, 50000]);
  const [kmsRange, setKmsRange] = useState([0, 200000]);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState('sw4'); // Por defecto SW4
  const [queries, setQueries] = useState([]);
  const [yearRange, setYearRange] = useState([1990, new Date().getFullYear()]);
  const [years, setYears] = useState([]);
  const [condition, setCondition] = useState('all'); // 'all', 'used', '0km', 'unpublished'
  
  // Estados para infinite scroll
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 20,
    total: 0,
    pages: 1,
    has_next: false,
    has_prev: false
  });
  
  // Ref para evitar cargas duplicadas
  const isLoadingRef = useRef(false);
  const currentQueryRef = useRef(selectedQuery);
  
  // Refs para mantener filtros actualizados en infinite scroll
  const filtersRef = useRef({
    searchTerm,
    sortBy,
    sortOrder,
    priceRange,
    kmsRange,
    yearRange
  });
  
  // Actualizar ref cuando cambia la query
  useEffect(() => {
    currentQueryRef.current = selectedQuery;
  }, [selectedQuery]);
  
  // Actualizar refs de filtros cuando cambian
  useEffect(() => {
    filtersRef.current = {
      searchTerm,
      sortBy,
      sortOrder,
      priceRange,
      kmsRange,
      yearRange,
      condition
    };
  }, [searchTerm, sortBy, sortOrder, priceRange, kmsRange, yearRange, condition]);
  
  // Estados para modal
  const [selectedCar, setSelectedCar] = useState(null);
  const [showModal, setShowModal] = useState(false);
  
  // Estados para panel de estadísticas
  const [showStats, setShowStats] = useState(false);
  
  // Estados para navegación
  const [currentSection, setCurrentSection] = useState('market'); // 'market' o 'prospection'
  
  // Estados para prospección desde mercado
  const [showProspectionForm, setShowProspectionForm] = useState(false);
  const [selectedCarForProspection, setSelectedCarForProspection] = useState(null);
  
  // Estados para notificaciones
  const [toast, setToast] = useState({ message: '', type: 'success', isVisible: false });

  useEffect(() => {
    loadQueries();
    // Cargar SW4 por defecto al iniciar
    loadCars();
  }, []);

  // Cuando cambia la categoría, cargar inmediatamente (sin debounce)
  useEffect(() => {
    if (selectedQuery) {
      resetAndLoadCars();
      loadYears();
    }
  }, [selectedQuery]);

  // Debounce unificado para TODOS los filtros (evita múltiples llamadas al mover sliders)
  useEffect(() => {
    if (!selectedQuery) return;
    const timeout = setTimeout(() => {
      resetAndLoadCars();
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchTerm, sortBy, sortOrder, priceRange, kmsRange, yearRange, condition]);

  const loadQueries = async () => {
    try {
      const data = await carService.getQueries();
      setQueries(data);
    } catch (err) {
      console.error('Error al cargar queries:', err.message);
    }
  };

  const loadYears = async () => {
    try {
      const data = await carService.getYears(selectedQuery);
      setYears(data);
      // NO resetear rango de años aquí para evitar loop
      // El rango se maneja manualmente por el usuario
    } catch (err) {
      console.error('Error al cargar años:', err.message);
      setYears([]);
    }
  };

  // Función para resetear y cargar desde el inicio
  const resetAndLoadCars = () => {
    // Cancelar cualquier carga en progreso
    isLoadingRef.current = false;
    setCurrentPage(1);
    setHasMore(true);
    loadCars(1, true);
  };

  // Función para cargar más autos (infinite scroll)
  const loadMoreCars = useCallback(() => {
    if (!isLoadingMore && hasMore && !isLoadingRef.current) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      loadCars(nextPage, false);
    }
  }, [currentPage, isLoadingMore, hasMore]);

  const loadCars = async (page = 1, isReset = false) => {
    // Evitar cargas duplicadas
    if (isLoadingRef.current && !isReset) {
      return;
    }
    
    isLoadingRef.current = true;
    const queryToUse = selectedQuery || 'sw4';
    const requestQuery = queryToUse;
    
    // Capturar condition actual
    const currentCondition = filtersRef.current.condition;
    
    // Si es despublicados, usar endpoint diferente
    if (currentCondition === 'unpublished') {
      try {
        if (isReset) { setCars([]); setFilteredCars([]); setLoading(true); }
        setError(null);
        const data = await carService.getUnpublishedCars(queryToUse);
        const newCars = data.cars || data || [];
        setCars(newCars);
        setFilteredCars(newCars);
        setHasMore(false);
        setPagination({ page: 1, per_page: newCars.length, total: newCars.length, pages: 1, has_next: false, has_prev: false });
      } catch (err) { setError(err.message); }
      finally { setLoading(false); setIsLoadingMore(false); isLoadingRef.current = false; }
      return;
    }
    
    // Usar refs para filtros en infinite scroll (evita closures stale)
    const currentFilters = filtersRef.current;
    
    try {
      // Si es reset, limpiar todo y mostrar loading completo
      if (isReset) {
        setCars([]);
        setFilteredCars([]);
        setLoading(true);
      } else {
        setIsLoadingMore(true);
      }
      setError(null);
      
      // Cargar desde la API real con todos los filtros
      const filters = {
        query: queryToUse,
        page: page,
        per_page: 20, // 20 autos por carga
        search: currentFilters.searchTerm,
        sort_by: currentFilters.sortBy,
        sort_order: currentFilters.sortOrder,
        min_price: currentFilters.priceRange[0] > 0 ? currentFilters.priceRange[0] : undefined,
        max_price: currentFilters.priceRange[1] < 50000 ? currentFilters.priceRange[1] : undefined,
        min_kms: currentFilters.kmsRange[0] > 0 ? currentFilters.kmsRange[0] : undefined,
        max_kms: currentFilters.kmsRange[1] < 200000 ? currentFilters.kmsRange[1] : undefined
      };
      
      // Agregar filtros de rango de años - siempre enviar ambos para filtrado preciso
      if (currentFilters.yearRange[0] && currentFilters.yearRange[1]) {
        filters.min_year = currentFilters.yearRange[0];
        filters.max_year = currentFilters.yearRange[1];
      }
      
      const response = await carService.getAllCars(filters);
      
      // Verificar si la query cambió mientras esperábamos la respuesta
      if (requestQuery !== currentQueryRef.current) {
        console.log('Query cambió durante la carga, descartando resultados');
        isLoadingRef.current = false;
        return;
      }
      
      // La respuesta ahora tiene estructura { cars: [...], pagination: {...} }
      if (response.cars) {
        if (isReset) {
          // Si es reset, reemplazar los autos
          setCars(response.cars);
          setFilteredCars(response.cars);
        } else {
          // Si es carga adicional, agregar a los existentes
          setCars(prev => [...prev, ...response.cars]);
          setFilteredCars(prev => [...prev, ...response.cars]);
        }
        setPagination(response.pagination);
        setHasMore(response.pagination.has_next);
      } else {
        // Fallback para respuesta sin paginación
        if (isReset) {
          setCars(response);
          setFilteredCars(response);
        } else {
          setCars(prev => [...prev, ...response]);
          setFilteredCars(prev => [...prev, ...response]);
        }
        setPagination({
          page: page,
          per_page: response.length,
          total: response.length,
          pages: 1,
          has_next: false,
          has_prev: false
        });
        setHasMore(false);
      }
      
      setLoading(false);
      setIsLoadingMore(false);
      isLoadingRef.current = false;
    } catch (err) {
      console.error('Error al conectar con la API:', err.message);
      setError('No se pudo conectar con la base de datos');
      setLoading(false);
      setIsLoadingMore(false);
      setHasMore(false);
      isLoadingRef.current = false;
    }
  };

  // Hook para infinite scroll
  const lastCarElementRef = useInfiniteScroll(loadMoreCars, hasMore, isLoadingMore || loading);

  // Funciones para manejar acciones de los autos
  const handleViewCar = (car) => {
    setSelectedCar(car);
    setShowModal(true);
  };

  const handleEditCar = (car) => {
    // Aquí implementarías la lógica de edición
    alert(`Editar: ${car.title}`);
  };

  const handleDeleteCar = (car) => {
    if (window.confirm(`¿Estás seguro de que quieres eliminar ${car.title}?`)) {
      setCars(cars.filter(c => c.id !== car.id));
    }
  };

  // Función para manejar prospección desde mercado
  const handleProspectCar = (car) => {
    setSelectedCarForProspection(car);
    setShowProspectionForm(true);
  };

  // Función para crear prospección
  const handleCreateProspectionFromMarket = async (formData) => {
    try {
      await prospectionService.createProspect(formData);
      // Mostrar mensaje de éxito
      setToast({
        message: '¡Vehículo prospectado exitosamente!',
        type: 'success',
        isVisible: true
      });
    } catch (error) {
      console.error('Error al crear prospección:', error);
      setToast({
        message: 'Error al prospectar el vehículo. Inténtalo de nuevo.',
        type: 'error',
        isVisible: true
      });
      throw error;
    }
  };

  const closeProspectionForm = () => {
    setShowProspectionForm(false);
    setSelectedCarForProspection(null);
  };

  const closeToast = () => {
    setToast(prev => ({ ...prev, isVisible: false }));
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedCar(null);
  };



  return (
    <div className="min-h-screen bg-dark-bg">
      {/* Fondo con gradiente */}
      <div className="absolute inset-0 bg-gradient-to-br from-dark-bg via-dark-card/20 to-dark-accent/30"></div>
      
      {/* Contenido principal */}
      <div className="relative z-10">
        <div className="container mx-auto px-4 py-8">
          <Header 
            carCount={pagination.total || cars.length} 
            onToggleStats={() => setShowStats(!showStats)}
            showStats={showStats}
            currentSection={currentSection}
            onSectionChange={setCurrentSection}
          />
          
          <main className="mt-8">
            {currentSection === 'market' ? (
              <>
                <SearchAndFilters
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  sortBy={sortBy}
                  setSortBy={setSortBy}
                  sortOrder={sortOrder}
                  setSortOrder={setSortOrder}
                  priceRange={priceRange}
                  setPriceRange={setPriceRange}
                  kmsRange={kmsRange}
                  setKmsRange={setKmsRange}
                  showFilters={showFilters}
                  setShowFilters={setShowFilters}
                  selectedQuery={selectedQuery}
                  setSelectedQuery={setSelectedQuery}
                  queries={queries}
                  yearRange={yearRange}
                  setYearRange={setYearRange}
                  years={years}
                />

                {/* Filtro condición + contador */}
                <div className="flex items-center justify-between mb-6 gap-2">
                  <div className="flex items-center bg-[#1c1c1a] border border-[#2a2926] rounded-lg overflow-x-auto no-scrollbar">
                    {[['all', 'Todos'], ['used', 'Usados'], ['0km', '0 Km'], ['unpublished', 'Desp.']].map(([val, label]) => (
                      <button
                        key={val}
                        onClick={() => setCondition(val)}
                        className={`px-2.5 sm:px-4 py-2 text-xs sm:text-sm font-medium whitespace-nowrap transition-colors ${
                          condition === val ? 'bg-[#2f6b4f] text-white' : 'text-[#8a8577] hover:text-[#e8e4de]'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  {pagination.total > 0 && (
                    <span className="text-sm text-[#8a8577] font-mono">{pagination.total} vehículos</span>
                  )}
                </div>
                
                {/* Panel de estadísticas */}
                <StatsPanel
                  selectedQuery={selectedQuery}
                  yearRange={yearRange}
                  isVisible={showStats}
                  onToggle={() => setShowStats(false)}
                />
                
                <CarGrid 
                  cars={cars}
                  loading={loading}
                  error={error}
                  onView={handleViewCar}
                  onEdit={handleEditCar}
                  onDelete={handleDeleteCar}
                  onProspect={handleProspectCar}
                  readOnly={true}
                  lastCarElementRef={lastCarElementRef}
                  isLoadingMore={isLoadingMore}
                  hasMore={hasMore}
                />
              </>
            ) : currentSection === 'motos' ? (
              <MotoMarket showStats={showStats} onToggleStats={() => setShowStats(false)} />
            ) : currentSection === 'seguimiento' ? (
              <Seguimiento />
            ) : currentSection === 'summary' ? (
              <MarketSummary />
            ) : (
              <Prospection />
            )}
          </main>
        </div>
        
        {/* Modal de detalles */}
        <CarModal
          car={selectedCar}
          isOpen={showModal}
          onClose={closeModal}
        />

        {/* Modal de prospección desde mercado */}
        <ProspectionFormFromMarket
          isOpen={showProspectionForm}
          onClose={closeProspectionForm}
          onSubmit={handleCreateProspectionFromMarket}
          carData={selectedCarForProspection}
        />

        {/* Toast de notificaciones */}
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={toast.isVisible}
          onClose={closeToast}
        />

        {/* Botón para volver arriba */}
        <ScrollToTop />

        {/* Banner de instalación PWA */}
        <InstallPrompt />
      </div>

      {/* Efectos de fondo */}
      <div className="fixed top-0 left-0 w-full h-full pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-dark-text/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl"></div>
      </div>
      
      {/* Debug Info - Deshabilitado */}
      {/* <DebugInfo /> */}
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <ProtectedRoute>
        <AppContent />
      </ProtectedRoute>
    </AuthProvider>
  );
}

export default App;
